"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Mail, Send, BookOpen, Sparkles, BookOpen as BookIcon, ArrowRight } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { Button } from "@/components/ui/button";

const PREVIEWS = [
  { num: "01", title: "Diagnose Before Treating", body: "The clinical method as the foundation of every operating decision.", date: "Issue 47" },
  { num: "02", title: "Triage Under Pressure", body: "Sequencing the work when everything is urgent and the room is reading the most senior person in it.", date: "Issue 46" },
  { num: "03", title: "The 70% Operating Model", body: "Why the talent the world overlooks produces the most reliable operational outcomes.", date: "Issue 45" },
];

export function OperatorsNotebookSection() {
  const [email, setEmail] = React.useState("");
  const [subscribed, setSubscribed] = React.useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.includes("@")) {
      setSubscribed(true);
      setTimeout(() => setSubscribed(false), 4000);
      setEmail("");
    }
  };

  return (
    <Section id="operators-notebook" ariaLabel="The Operator's Notebook" background="muted">
      <div className="grid lg:grid-cols-[1fr_1fr] gap-10 items-center">
        <div>
          <SectionHeader
            icon={BookIcon}
            overline="The Operator's Notebook"
            overlineClassName="text-amber-gold"
            pillClassName="glass-tinted-amber-gold border-amber-gold/20"
            align="left"
            title={
              <>
                One principle. <br />
                <span className="text-gradient-premium">Every two weeks.</span>
              </>
            }
            subtitle="Biweekly notes on how excellent things run. Drawn from a decade of sovereign-scale operations. Made usable for any organization."
          />

          <form onSubmit={onSubmit} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md">
            <div className="relative flex-1">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 h-12 rounded-lg glass border border-border/60 text-body-md focus:border-red focus:outline-none focus:ring-2 focus:ring-red/30 transition-all"
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="h-12 px-6 bg-gradient-sovereign shadow-lg shadow-red/30"
              disabled={subscribed}
            >
              {subscribed ? <><Sparkles className="size-4" />Subscribed</> : <><Send className="size-4" />Subscribe</>}
            </Button>
          </form>

          <p className="mt-3 text-caption text-muted-foreground">
            Subscribers receive one clinical-grade principle per installment. Unsubscribe at any time.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="glass-strong rounded-2xl p-6 md:p-7 gradient-border relative overflow-hidden">
            <div className="flex items-center gap-2 mb-5">
              <BookOpen className="size-5 text-amber-gold" />
              <span className="text-overline text-amber-gold">Recent Issues</span>
            </div>
            <div className="space-y-3">
              {PREVIEWS.map((p) => (
                <div key={p.num} className="p-4 rounded-lg bg-muted/30 border border-border/30 hover:border-amber-gold/30 transition-colors">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-caption font-bold text-amber-gold tracking-wider uppercase">No. {p.num}</span>
                    <span className="text-caption text-muted-foreground">{p.date}</span>
                  </div>
                  <h4 className="text-heading-sm mb-1.5">{p.title}</h4>
                  <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
            <div className="pt-4 mt-4 border-t border-border/30 text-center">
              <Link href="/the-operators-notebook/" className="inline-flex items-center gap-1.5 text-caption font-semibold text-amber-gold hover:gap-2.5 transition-all group">
                See the full archive
                <ArrowRight className="size-3.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
        {[
          { cls: "text-lime", label: "Operations" },
          { cls: "text-indigo", label: "Strategy" },
          { cls: "text-denim", label: "Leadership" },
          { cls: "text-cyan", label: "Execution" },
          { cls: "text-amber-gold", label: "Doctrine" },
          { cls: "text-amber-gold", label: "Composition" },
        ].map((s) => (
          <span key={s.label} className="glass rounded-full px-3 py-1.5 text-caption font-medium">
            <span className={s.cls}>·</span> <span className="text-muted-foreground ml-1">{s.label}</span>
          </span>
        ))}
      </div>
    </Section>
  );
}
